import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Modal from 'react-bootstrap/Modal'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import { toggleTransfer } from 'store/redux/moneyPoolBox'

/**
 * Which kind of box? — the `+` in the funds header used to go straight to the
 * moneypool wizard, which is why صندوق قرعه‌کشی could not be created from the
 * PWA at all.
 *
 * The two products differ in how people join, and the copy says so: a
 * moneypool has a join link, a cashbox does not — its manager adds every
 * member by phone number.
 *
 * `?transfer=1` is «انتقال صندوق به همیان» (Android `AddBoxActivity` with
 * `IS_TRANSFER_BOX`). A moneypool then runs the same wizard and ends on
 * «ثبت سوابق مدیر»; a cashbox cannot be transferred yet, and says so with
 * Android's own sentence instead of opening a create form that would make a
 * brand-new box.
 */
export default function ChooseBoxTypePage() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [searchParams] = useSearchParams()
    const isTransfer = searchParams.get('transfer') === '1'
    const [showCashboxNotice, setShowCashboxNotice] = useState(false)

    // Written on every visit, not only when true: the wizard's store is
    // persisted, and a transfer abandoned last week must not turn today's
    // new fund into one.
    useEffect(() => {
        dispatch(toggleTransfer(isTransfer))
    }, [dispatch, isTransfer])

    const choose = (boxKind: 'moneypool' | 'cashbox') => {
        posthog.capture('fund_box_type_selected', {
            box_kind: boxKind,
            is_transfer: isTransfer,
        })
        if (boxKind === 'moneypool') {
            navigate('/add-box/moneyPool')
        } else if (isTransfer) {
            posthog.capture('fund_transfer_cashbox_unavailable')
            setShowCashboxNotice(true)
        } else {
            navigate('/cashbox/create')
        }
    }

    return (
        <div className="bg-white min-h-screen">
            <Header
                title={isTransfer ? 'انتقال صندوق به همیان' : 'ساخت صندوق'}
            />

            <div className="px-4 pt-2">
                {isTransfer && (
                    <p className="text-sm text-text-secondary leading-6 mb-3">
                        صندوقی که بیرون از همیان مدیرش هستید از کدام نوع است؟
                    </p>
                )}

                <button
                    type="button"
                    onClick={() => choose('moneypool')}
                    className="w-full text-right rounded-card border border-divider bg-white p-4 mb-3 active:scale-[0.99]"
                >
                    <p className="text-base font-bold text-black">
                        صندوق قرض‌الحسنه
                    </p>
                    <p className="text-sm text-text-secondary mt-1 leading-6">
                        اعضا حق عضویت ماهانه می‌پردازند و از محل سرمایه‌ی صندوق
                        وام می‌گیرند. عضوگیری با لینک هم ممکن است.
                    </p>
                </button>

                <button
                    type="button"
                    onClick={() => choose('cashbox')}
                    className="w-full text-right rounded-card border border-divider bg-white p-4 active:scale-[0.99]"
                >
                    <p className="text-base font-bold text-black">
                        صندوق قرعه‌کشی
                    </p>
                    <p className="text-sm text-text-secondary mt-1 leading-6">
                        هر نوبت همه سهم خود را می‌پردازند و با قرعه‌کشی، وام به
                        یک یا چند نفر می‌رسد. اعضا را فقط مدیر اضافه می‌کند و
                        لینک عضویت ندارد.
                    </p>
                </button>
            </div>

            <Modal
                show={showCashboxNotice}
                onHide={() => setShowCashboxNotice(false)}
                size="sm"
                centered
            >
                <div className="p-4 text-center">
                    <p className="text-sm text-text-body leading-7">
                        این سرویس در حال حاضر در همیان ارائه نمی‌شود.
                    </p>
                    <button
                        type="button"
                        onClick={() => setShowCashboxNotice(false)}
                        className="w-full mt-4 bg-green-1 text-white rounded-full py-2 text-sm font-bold"
                    >
                        متوجه شدم
                    </button>
                </div>
            </Modal>
        </div>
    )
}
